/**
 * `Space<P>` — the grid-agnostic geometry interface.
 *
 * Lancer defines everything in "spaces" — "hexes **or** squares on a grid map"
 * (core book p. 59). `Space<P>` is that sentence as a type: a minimal set of
 * operations the rules/encounter layers call, written **once** against the
 * interface and never against a concrete coordinate's internals. Two integer
 * implementations realize it — {@link hexSpace} and the square grid. An
 * encounter picks one grid at creation, so this is parameterization, not
 * runtime polymorphism within a fight.
 *
 * **Render stays out of `Space`.** Pixel layout (`hexToPixel`/`corners`) is a
 * separate per-grid `Layout` concern — the rules engine never imports it. This
 * preserves the package's rules-free / render-free split.
 */
export type Space<P> = {
  // serialization
  /**
   * Canonical string key for a cell. **Contract invariants** (asserted by
   * `runSpaceContract`, and relied on by `edge.ts`'s grid-agnostic `edgeKey`):
   * `key`∘`parse` is the identity and `parse`∘`key` round-trips through
   * {@link equals}; the key string must **never contain the `|` separator**
   * `edgeKey` joins two keys with; and `<=` on keys must sort stably so the
   * unordered edge `{a,b}` collapses to one canonical key regardless of order.
   */
  key(p: P): string;
  parse(k: string): P;
  equals(a: P, b: P): boolean;

  /** Grid distance in spaces (hex distance | Chebyshev). */
  distance(a: P, b: P): number;
  /** Adjacent cells (6 for hex | 8 for square). */
  neighbors(p: P): P[];
  /**
   * The cells **flanking** a single adjacent step `from → to` — the cells an L-shaped
   * orthogonal detour would route through instead of cutting the corner. Non-empty
   * **only** for a corner-cutting step (a square diagonal → its two orthogonal
   * neighbours); `[]` for an edge-adjacent step (every hex step, every orthogonal
   * square step — those cut no corner). Pure geometry; the **no-corner-squeeze**
   * movement rule (a diagonal step is obstructed when *every* flank is blocked, so a
   * unit may round a single corner but not squeeze between two diagonally-placed
   * blockers) is layered on this in {@link reachableAnchors}. Each returned flank is at
   * distance 1 from both `from` and `to`.
   */
  diagonalFlanks(from: P, to: P): P[];
  /** The connected line of cells from `a` to `b` inclusive (LoS spine). */
  line(a: P, b: P): P[];
  /** All cells within radius `r` — BLAST/BURST templates. */
  disk(center: P, r: number): Region<P>;
  /** The cells a unit of Lancer **Size** `size` occupies, anchored at `anchor`. */
  footprint(anchor: P, size: number): Region<P>;
  /**
   * Displace `p` by the `from → to` vector: `p + (to − from)`. `pushDestination`
   * (`packages/rules/src/encounter/effects/spatialAnswerer.ts`) deliberately avoids
   * coordinate algebra — it walks a *ray* one step at a time via `neighbors`/`line`/
   * `distance` — but the grapple drag's strict mirror needs exactly this one
   * primitive: "take the draggee's anchor and slide it by the same vector the mover
   * just moved." Laws (asserted by `runSpaceContract`): `translate(p, from, from) === p`;
   * `translate(from, from, to) === to`; and `translate` is an isometry —
   * `distance(translate(a,f,t), translate(b,f,t)) === distance(a,b)`.
   */
  translate(p: P, from: P, to: P): P;
}

/**
 * A set of cells. `contains` is the **primitive** (and the seam a future
 * gridless impl would satisfy without a `cells()` enumeration); `cells()` is
 * the renderer's/highlighter's affordance — first-class here because every
 * shipped grid is discrete.
 */
export type Region<P> = {
  contains(p: P): boolean;
  cells(): P[];
}

/**
 * Build a {@link Region} from an explicit, finite cell list and a key function.
 * The standard `Region` for discrete grids — `contains` is an O(1) set lookup,
 * `cells()` returns the original list.
 */
export function cellRegion<P>(cells: P[], key: (p: P) => string): Region<P> {
  const keys = new Set(cells.map(key));
  return {
    contains: (p) => keys.has(key(p)),
    cells: () => cells,
  };
}
